import { isAddress } from "viem";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isSolanaAddress } from "./address";

/**
 * Fallback Solana action IDs — used when action definitions aren't available
 * or when the `platform` field is missing from the API response.
 * IMPORTANT: Update this list when adding new Solana actions.
 * TODO: Read from action definitions API `platform` field once Go serializes it.
 */
const SOLANA_ACTION_IDS_FALLBACK = [
  "send-sol",
  "send-spl-token",
  "jupiter-swap",
  "jupiter-quote",
  "get-sol-balance",
  "get-spl-token-balance",
  "get-spl-token-metadata",
];

/**
 * Returns true if the given nodeType string refers to a Solana action.
 * Checks action definitions `platform` field first (authoritative),
 * falls back to hardcoded list when definitions aren't available.
 */
export function isSolanaNodeType(
  nodeType: string,
  nodeDefinitions?: Array<{ type: string; platform?: string }>,
): boolean {
  if (!nodeType) return false;
  // Authoritative check via definitions (if platform field is populated)
  if (nodeDefinitions) {
    const def = nodeDefinitions.find(d => d.type === nodeType);
    if (def?.platform) return def.platform === "solana";
  }
  // Fallback: hardcoded list (used when definitions lack platform field or aren't available)
  return SOLANA_ACTION_IDS_FALLBACK.some(id => nodeType === id || nodeType.startsWith(id + "-"));
}

export type ChainFamily = "evm" | "solana" | "unknown";

/**
 * Detect chain family from a wallet address string.
 * EVM addresses start with 0x (42 hex chars).
 * Solana addresses are base58 (32-44 chars).
 */
export function getChainFamily(address: string): ChainFamily {
  if (!address) return "unknown";
  if (isAddress(address)) return "evm";
  if (isSolanaAddress(address)) return "solana";
  return "unknown";
}

/**
 * Check if a wallet address matches a given chain ID.
 * Solana chain IDs: 7565164 (mainnet), 7565165 (devnet)
 * Everything else: EVM
 */
export function isWalletCompatibleWithChain(address: string, chainId: number | undefined): boolean {
  if (!chainId) return true; // No chain selected, show all
  const family = getChainFamily(address);
  if (family === "unknown") return true; // Can't determine, show it

  const isSolanaChain = SOLANA_CHAIN_IDS.has(chainId);
  return isSolanaChain ? family === "solana" : family === "evm";
}

/**
 * Filter a list of wallets to only those compatible with a chain ID.
 */
export function filterWalletsByChain<T extends { address: string }>(wallets: T[], chainId: number | undefined): T[] {
  if (!chainId) return wallets;
  return wallets.filter(w => isWalletCompatibleWithChain(w.address, chainId));
}

// Mirror of @b3dotfun/sdk/anyspend/constants — Relay/AnySpend's internal chain id for Solana.
const RELAY_SOLANA_MAINNET_CHAIN_ID = 792703809;
const RELAY_SOL_NATIVE_ADDRESS = "11111111111111111111111111111111";
const EVM_DEFAULT_CHAIN_ID = 8453; // Base
const EVM_NATIVE_ADDRESS = "0x0000000000000000000000000000000000000000";

/**
 * Default destination token+chain for an AnySpend deposit into a wallet.
 *
 * AnySpend's QR-code deposit path needs a real
 * `destinationTokenChainId`/`destinationTokenAddress` pair — passing `0`/`""`
 * makes createOrder reject with "Invalid dstChain" / "Invalid recipientAddress".
 * Falling back to native token on the wallet's own chain makes the QR view
 * a pure-transfer screen (no bridge order).
 */
export function getDefaultDepositDestination(walletAddress: string): {
  chainId: number;
  tokenAddress: string;
} {
  if (getChainFamily(walletAddress) === "solana") {
    return { chainId: RELAY_SOLANA_MAINNET_CHAIN_ID, tokenAddress: RELAY_SOL_NATIVE_ADDRESS };
  }
  return { chainId: EVM_DEFAULT_CHAIN_ID, tokenAddress: EVM_NATIVE_ADDRESS };
}
